import type {
  GSplatEffectTemplate,
  GSplatEffectTemplateColorControl,
} from '@sharefe/share3d-engine'
import type {
  GS3DEffectAnimationPresetColorControlInput,
  GS3DEffectAnimationPresetState,
} from './gs3dEffectAnimationPreset'
import { cloneParticleScatterParams } from './gs3dEffectAnimationPreset'

export type GSplatEffectTemplatePresetEffectState = GS3DEffectAnimationPresetState['effect']

export function parseGSplatEffectTemplateImportJson(source: string): unknown {
  try {
    return JSON.parse(source)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(`effect template JSON parse failed: ${message}`)
  }
}

export function applyGSplatEffectTemplateToPresetEffectState(
  current: GSplatEffectTemplatePresetEffectState,
  template: GSplatEffectTemplate,
): GSplatEffectTemplatePresetEffectState {
  const next = clonePresetEffectState(current)
  next.selectedEffectKey = template.effectKey
  next.adaptive = template.adaptive ?? true

  switch (template.effectKey) {
    case 'box-reveal':
    case 'box-hide':
    case 'box-tint':
    case 'box-untint':
      next.box = {
        size: template.params.size,
        direction: template.params.direction,
        duration: template.params.duration,
        tintColor: template.params.tintColor,
        edgeColor: template.params.edgeColor,
      }
      break
    case 'box-crop':
      next.box = {
        ...next.box,
        size: template.params.size,
      }
      break
    case 'reveal-radial':
      if (template.adaptive === false) {
        next.revealManual.radial = {
          speed: template.params.speed,
          acceleration: template.params.acceleration,
          delay: template.params.delay,
          dotTint: toPresetColorControlInput(template.params.dotTint),
          waveTint: toPresetColorControlInput(template.params.waveTint),
          oscillationIntensity: template.params.oscillationIntensity,
          bandWidth: template.params.bandWidth,
        }
      } else {
        next.reveal.radial = {
          duration: template.params.duration,
          dotTint: toPresetColorControlInput(template.params.dotTint),
          waveTint: toPresetColorControlInput(template.params.waveTint),
          intensity: template.params.intensity,
        }
      }
      break
    case 'reveal-rain':
      if (template.adaptive === false) {
        next.revealManual.rain = {
          distance: template.params.distance,
          speed: template.params.speed,
          acceleration: template.params.acceleration,
          flightTime: template.params.flightTime,
          rainSize: template.params.rainSize,
          rotation: template.params.rotation,
          fallTint: toPresetColorControlInput(template.params.fallTint),
          fallTintIntensity: template.params.fallTintIntensity,
          hitTint: toPresetColorControlInput(template.params.hitTint),
          hitDuration: template.params.hitDuration,
        }
      } else {
        next.reveal.rain = {
          duration: template.params.duration,
          fallTint: toPresetColorControlInput(template.params.fallTint),
          fallTintIntensity: template.params.fallTintIntensity,
          hitTint: toPresetColorControlInput(template.params.hitTint),
          intensity: template.params.intensity,
        }
      }
      break
    case 'reveal-grid':
      if (template.adaptive === false) {
        next.revealManual.grid = {
          blockSize: template.params.blockSize,
          delay: template.params.delay,
          duration: template.params.duration,
          dotSize: template.params.dotSize,
          moveTint: toPresetColorControlInput(template.params.moveTint),
          moveTintIntensity: template.params.moveTintIntensity,
          landTint: toPresetColorControlInput(template.params.landTint),
          landDuration: template.params.landDuration,
        }
      } else {
        next.reveal.grid = {
          duration: template.params.duration,
          moveTint: toPresetColorControlInput(template.params.moveTint),
          moveTintIntensity: template.params.moveTintIntensity,
          landTint: toPresetColorControlInput(template.params.landTint),
          intensity: template.params.intensity,
        }
      }
      break
    case 'obb-double-wave':
      if (template.adaptive === false) {
        next.revealManual.obb = {
          speed: template.params.speed,
          acceleration: template.params.acceleration,
          delay: template.params.delay,
          dotTint: toPresetColorControlInput(template.params.dotTint),
          waveTint: toPresetColorControlInput(template.params.waveTint),
          oscillationIntensity: template.params.oscillationIntensity,
          bandWidth: template.params.bandWidth,
        }
      } else {
        next.reveal.obb = {
          duration: template.params.duration,
          dotTint: toPresetColorControlInput(template.params.dotTint),
          waveTint: toPresetColorControlInput(template.params.waveTint),
          intensity: template.params.intensity,
        }
      }
      break
    case 'visual-electronic':
    case 'visual-deep-meditation':
    case 'visual-waves':
    case 'visual-flare':
    case 'visual-disintegrate':
      next.visual[template.effectKey] = {
        intensity: template.params.intensity,
        timeScale: template.params.timeScale,
        ...(template.effectKey === 'visual-electronic' ? {
          swayAmplitude: template.params.swayAmplitude,
          swaySpeed: template.params.swaySpeed,
          flickerColor: template.params.flickerColor === undefined ?
            undefined :
            toPresetColorControlInput(template.params.flickerColor),
        } : {}),
      }
      break
    case 'reveal-magic':
    case 'reveal-spread':
    case 'reveal-unroll':
    case 'reveal-twister':
      next.showcaseReveal[template.effectKey] = {
        timeScale: template.params.timeScale,
        motionScale: template.params.motionScale,
        duration: template.params.duration,
      }
      break
    case 'particle-scatter-reassemble':
      next.particle[template.effectKey] = cloneParticleScatterParams(template.params)
      break
    case 'dissolve-drift':
      next.dissolve[template.effectKey] = {
        speed: template.params.speed,
        motionScale: template.params.motionScale,
      }
      break
  }

  return next
}

export function toPresetColorControlInput(
  control: GSplatEffectTemplateColorControl,
): GS3DEffectAnimationPresetColorControlInput {
  return {
    hex: control.color,
    intensity: control.intensity,
  }
}

export function getGSplatEffectTemplateBoxCropEdgeScaleFactor(
  template: GSplatEffectTemplate,
): number | null {
  return template.effectKey === 'box-crop' ? template.params.edgeScaleFactor : null
}

function clonePresetEffectState(
  current: GSplatEffectTemplatePresetEffectState,
): GSplatEffectTemplatePresetEffectState {
  return {
    selectedEffectKey: current.selectedEffectKey,
    adaptive: current.adaptive,
    box: { ...current.box },
    reveal: {
      radial: {
        ...current.reveal.radial,
        dotTint: { ...current.reveal.radial.dotTint },
        waveTint: { ...current.reveal.radial.waveTint },
      },
      rain: {
        ...current.reveal.rain,
        fallTint: { ...current.reveal.rain.fallTint },
        hitTint: { ...current.reveal.rain.hitTint },
      },
      grid: {
        ...current.reveal.grid,
        moveTint: { ...current.reveal.grid.moveTint },
        landTint: { ...current.reveal.grid.landTint },
      },
      obb: {
        ...current.reveal.obb,
        dotTint: { ...current.reveal.obb.dotTint },
        waveTint: { ...current.reveal.obb.waveTint },
      },
    },
    revealManual: {
      radial: {
        ...current.revealManual.radial,
        dotTint: { ...current.revealManual.radial.dotTint },
        waveTint: { ...current.revealManual.radial.waveTint },
      },
      rain: {
        ...current.revealManual.rain,
        fallTint: { ...current.revealManual.rain.fallTint },
        hitTint: { ...current.revealManual.rain.hitTint },
      },
      grid: {
        ...current.revealManual.grid,
        moveTint: { ...current.revealManual.grid.moveTint },
        landTint: { ...current.revealManual.grid.landTint },
      },
      obb: {
        ...current.revealManual.obb,
        dotTint: { ...current.revealManual.obb.dotTint },
        waveTint: { ...current.revealManual.obb.waveTint },
      },
    },
    visual: {
      'visual-electronic': { ...current.visual['visual-electronic'] },
      'visual-deep-meditation': { ...current.visual['visual-deep-meditation'] },
      'visual-waves': { ...current.visual['visual-waves'] },
      'visual-flare': { ...current.visual['visual-flare'] },
      'visual-disintegrate': { ...current.visual['visual-disintegrate'] },
    },
    showcaseReveal: {
      'reveal-magic': { ...current.showcaseReveal['reveal-magic'] },
      'reveal-spread': { ...current.showcaseReveal['reveal-spread'] },
      'reveal-unroll': { ...current.showcaseReveal['reveal-unroll'] },
      'reveal-twister': { ...current.showcaseReveal['reveal-twister'] },
    },
    particle: {
      'particle-scatter-reassemble': cloneParticleScatterParams(
        current.particle['particle-scatter-reassemble'],
      ),
    },
    dissolve: {
      'dissolve-drift': { ...current.dissolve['dissolve-drift'] },
    },
  }
}
